import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { getUnreadAlertCountV2 } from '../../api/alerts';
import {
  selectConnectionStatus,
  type ConnectionStatus,
} from '../../store/slices/priceStreamSlice';
import CurrencyBadge from '../common/CurrencyBadge';

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2a5 5 0 00-5 5v3l-2 3h14l-2-3V7a5 5 0 00-5-5z" /><path d="M8 15a2 2 0 004 0" />
  </svg>
);

/** Maps connection status → dot colour + label */
const STATUS_CONFIG: Record<
  ConnectionStatus,
  { dotClass: string; pulseClass: string; label: string | null; tooltip: string | null }
> = {
  idle:         { dotClass: 'bg-gray-600',    pulseClass: '',               label: null,             tooltip: null },
  connected:    { dotClass: 'bg-emerald-400', pulseClass: '',               label: null,             tooltip: 'Live prices connected' },
  reconnecting: { dotClass: 'bg-amber-400',   pulseClass: 'ws-dot-pulse',   label: 'Reconnecting…', tooltip: 'Reconnecting to live prices…' },
  disconnected: { dotClass: 'bg-red-400',     pulseClass: '',               label: 'Disconnected',   tooltip: 'Disconnected — showing last known prices' },
};

/**
 * Shows a small 8 px WebSocket connection status dot in the page header.
 * - Green: hides after 5 s of stable connection.
 * - Amber + pulse: reconnecting, shown persistently.
 * - Red: disconnected, shown persistently.
 * - Hidden before first connection attempt (status === 'idle').
 */
const WsStatusDot: React.FC = () => {
  const status = useAppSelector(selectConnectionStatus);
  const [hidden, setHidden] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (status === 'idle') {
      setHidden(true);
      return;
    }

    // Show the dot whenever status changes to something non-idle
    setHidden(false);

    if (status === 'connected') {
      // Auto-hide after 5 s when stable
      hideTimerRef.current = setTimeout(() => {
        setHidden(true);
        hideTimerRef.current = null;
      }, 5000);
    }

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [status]);

  if (hidden) return null;

  const { dotClass, pulseClass, label, tooltip } = STATUS_CONFIG[status];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={tooltip ?? undefined}
      className="flex items-center gap-1.5"
      title={tooltip ?? undefined}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dotClass} ${pulseClass}`}
      />
      {label && (
        <span className="text-xs text-text-muted hidden sm:inline">{label}</span>
      )}
    </div>
  );
};

const routeTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/wallet-hub': 'Assets & Wallets',
  '/wallets': 'Wallets',
  '/risk-intelligence': 'Risk Intelligence',
  '/risk': 'Risk Analysis',
  '/risk-analytics': 'Advanced Risk Analytics',
  '/defi-health': 'DeFi Health',
  '/transactions': 'Transactions',
  '/alerts': 'Alerts',
  '/alert-rules': 'Alert Rules',
  '/automation': 'Automation Rules',
  '/rebalancing-planner': 'Rebalancing Planner',
  '/notifications': 'Notification Settings',
  '/audit-logs': 'Audit Logs',
  '/admin/audit-dashboard': 'Audit Dashboard',
  '/privacy-settings': 'Privacy Settings',
  '/data-export': 'Data Export',
  '/account-deletion': 'Account Deletion',
  '/security-defenses': 'Security Defenses',
  // Beta account-state routes
  '/billing/invoices': 'Beta Access',
  '/billing/success': 'Beta Access',
  '/billing/subscription': 'Beta Access',
  '/billing/plans': 'Beta Access',
  '/billing/upgrade': 'Beta Access',
};

const PageHeader: React.FC<{ onMobileMenuOpen?: () => void }> = ({ onMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const title = routeTitles[location.pathname] || 'Page';

  useEffect(() => {
    if (!user?.id) return;
    const fetch = async () => {
      try {
        const count = await getUnreadAlertCountV2(user.id);
        setUnreadCount(count);
      } catch {
        // non-blocking
      }
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
    window.location.reload();
  };

  return (
    <header className="flex items-center justify-between h-14 px-4 md:px-6 border-b border-border bg-surface/80 backdrop-blur-sm sticky top-0 z-30">
      <div className="flex items-center gap-3">
        {/* Hamburger button — visible on mobile only */}
        <button
          onClick={onMobileMenuOpen}
          className="md:hidden p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
          aria-label="Open navigation menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5h14M3 10h14M3 15h14" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold text-text-primary">{title}</h1>
      </div>

      <div className="flex items-center gap-3">
        {/* WebSocket connection status */}
        <WsStatusDot />

        {/* Active currency badge */}
        <CurrencyBadge />

        {/* Notification bell */}
        <button
          onClick={() => navigate('/alerts')}
          className="relative p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
          aria-label="Notifications"
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-danger text-[10px] font-bold text-white px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-surface-hover transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-sm font-semibold text-white">
              {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
            </div>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-surface border border-border rounded-lg shadow-lg py-1 animate-pop-in z-50">
              <div className="px-4 py-2 border-b border-border">
                <p className="text-sm font-medium text-text-primary truncate">
                  {user?.displayName || user?.email}
                </p>
                <p className="text-xs text-text-muted truncate">{user?.email}</p>
              </div>
              <div className="border-t border-border" />
              <button
                onClick={() => { navigate('/privacy-settings'); setMenuOpen(false); }}
                className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
              >
                Privacy Settings
              </button>
              <button
                onClick={() => { navigate('/notifications'); setMenuOpen(false); }}
                className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
              >
                Notification Settings
              </button>
              <div className="border-t border-border" />
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-2 text-sm text-danger hover:bg-surface-hover transition-colors"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
